import { CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Form, Input, message, Modal, Select, Space, Table, Tag } from 'antd';
import { useState } from 'react';

import { axiosClient } from '@/api/axiosClient';

// V1.4 D2.2: BE LEFT JOINs worker + factory so this table shows real names
// instead of UUID slices.
interface LeaveRequestRow {
  id: string;
  worker_id: string;
  type_code: string;
  from_date: string;
  to_date: string;
  reason?: string;
  status: string;
  submitted_at?: string;
  approved_by?: string;
  worker_name?: string;
  worker_phone?: string;
  factory_name?: string;
}

const STATUS_COLOR: Record<string, string> = {
  APPROVED: 'green',
  AUTO_KP: 'orange',
  AUTO_NKP: 'orange',
  AUTO_NKP_NV: 'red',
  AUTO_NP: 'orange',
  PENDING: 'blue',
  REJECTED: 'red',
};

// V1.4 D4: complete the auto-* status set per spec §6.1 Phần III. Original
// FE only had AUTO_NKP; spec defines AUTO_NP / AUTO_KP / AUTO_NKP_NV too.
const ALL_STATUSES = [
  'PENDING',
  'APPROVED',
  'REJECTED',
  'AUTO_NP',
  'AUTO_NKP',
  'AUTO_KP',
  'AUTO_NKP_NV',
];

// T-VH-06 — QLĐ duyệt đơn nghỉ phép / nghỉ việc. NLĐ submit via App
// (POST /leave-request/create) — Web admin uses this page to approve/reject.
const LeaveRequestPage = () => {
  const [status, setStatus] = useState<string>('PENDING');
  const [rejectId, setRejectId] = useState<string>('');
  const [rejectReason, setRejectReason] = useState<string>('');

  // QC #32: Add edit status modal
  const [editRow, setEditRow] = useState<LeaveRequestRow | null>(null);
  const [editForm] = Form.useForm();

  const { data, isLoading, refetch } = useQuery(
    ['leave-requests', status],
    () =>
      axiosClient.get<{ rows: LeaveRequestRow[]; total_rows: number }>(
        `/leave-request/filter?status=${status}`
      ),
    { refetchOnWindowFocus: false }
  );

  const approveMutation = useMutation(
    (id: string) => axiosClient.patch('/leave-request/approve', { id }),
    { onSuccess: () => { message.success('Đã duyệt đơn'); refetch(); setEditRow(null); } }
  );
  const rejectMutation = useMutation(
    ({ id, reason }: { id: string; reason: string }) =>
      axiosClient.patch('/leave-request/reject', { id, reason }),
    {
      onSuccess: () => {
        message.success('Đã từ chối đơn');
        setRejectId('');
        setRejectReason('');
        setEditRow(null);
        refetch();
      },
    }
  );

  const handleSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      if (values.status === 'APPROVED') {
        approveMutation.mutate(editRow!.id);
      } else if (values.status === 'REJECTED') {
        rejectMutation.mutate({ id: editRow!.id, reason: values.reason || '' });
      }
    } catch (e) {
      // Validate error
    }
  };

  const columns = [
    {
      key: 'worker',
      render: (_: unknown, row: LeaveRequestRow) => (
        <span>
          {row.worker_name || row.worker_id.slice(0, 8) + '…'}
          {row.worker_phone && (
            <span style={{ color: '#999', fontSize: 12, marginLeft: 6 }}>
              {row.worker_phone}
            </span>
          )}
        </span>
      ),
      title: 'NLĐ',
    },
    { dataIndex: 'factory_name', key: 'factory', title: 'Nhà máy' },
    { dataIndex: 'type_code', key: 'type', title: 'Loại' },
    { dataIndex: 'from_date', key: 'from', title: 'Từ ngày' },
    { dataIndex: 'to_date', key: 'to', title: 'Đến ngày' },
    { dataIndex: 'reason', key: 'reason', title: 'Lý do' },
    {
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
      title: 'Trạng thái',
    },
    {
      key: 'actions',
      render: (_: unknown, row: LeaveRequestRow) => (
        <Space>
          {row.status === 'PENDING' && (
            <>
              <Button
                icon={<CheckCircleOutlined rev="" />}
                loading={approveMutation.isLoading}
                onClick={() => approveMutation.mutate(row.id)}
                size="small"
                type="primary"
              >
                Duyệt
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined rev="" />}
                onClick={() => setRejectId(row.id)}
                size="small"
              >
                Từ chối
              </Button>
            </>
          )}
          <Button
            icon={<EditOutlined rev="" />}
            onClick={() => {
              setEditRow(row);
              editForm.setFieldsValue({
                status: ['APPROVED', 'REJECTED'].includes(row.status) ? row.status : undefined,
                reason: row.reason || '',
              });
            }}
            size="small"
          >
            Sửa
          </Button>
        </Space>
      ),
      title: 'Hành động',
    },
  ];

  return (
    <Card
      extra={
        <Select
          onChange={setStatus}
          options={ALL_STATUSES.map((s) => ({ label: s, value: s }))}
          style={{ width: 200 }}
          value={status}
        />
      }
      title="Duyệt nghỉ phép / nghỉ việc (T-VH-06)"
    >
      <Table<LeaveRequestRow>
        columns={columns}
        dataSource={
          (data as unknown as { data?: { rows: LeaveRequestRow[] } })?.data?.rows || []
        }
        // QC #32 — empty default view was being read as "feature broken".
        // The page IS working; there just aren't any pending requests yet
        // because no worker has submitted from the app on this env. Spell
        // it out so the tester knows where rows are supposed to come from.
        locale={{
          emptyText: (
            <div style={{ padding: 24, color: '#888' }}>
              Chưa có đơn{' '}
              {status === 'PENDING' ? 'đang chờ duyệt' : `ở trạng thái ${status}`}.
              <br />
              NLĐ submit đơn nghỉ từ App sẽ hiển thị ở đây để QLĐ duyệt / từ chối.
            </div>
          ),
        }}
        loading={isLoading}
        rowKey="id"
        size="middle"
      />

      <Modal
        cancelText="Huỷ"
        confirmLoading={rejectMutation.isLoading}
        okText="Từ chối"
        onCancel={() => setRejectId('')}
        onOk={() => rejectMutation.mutate({ id: rejectId, reason: rejectReason })}
        open={!!rejectId}
        title="Từ chối đơn nghỉ"
      >
        <Input.TextArea
          onChange={(e) => setRejectReason(e.target.value)}
          placeholder="Lý do từ chối..."
          rows={3}
          value={rejectReason}
        />
      </Modal>

      <Modal
        cancelText="Huỷ"
        confirmLoading={approveMutation.isLoading || rejectMutation.isLoading}
        okText="Cập nhật"
        onCancel={() => setEditRow(null)}
        onOk={handleSaveEdit}
        open={!!editRow}
        title="Cập nhật trạng thái đơn nghỉ phép"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            label="Trạng thái"
            name="status"
            rules={[{ message: 'Vui lòng chọn trạng thái', required: true }]}
          >
            <Select placeholder="Chọn trạng thái">
              <Select.Option value="APPROVED">Phê duyệt</Select.Option>
              <Select.Option value="REJECTED">Từ chối</Select.Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.status !== currentValues.status}
          >
            {({ getFieldValue }) =>
              getFieldValue('status') === 'REJECTED' ? (
                <Form.Item label="Lý do từ chối" name="reason">
                  <Input.TextArea placeholder="Nhập lý do từ chối..." rows={3} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LeaveRequestPage;
